import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  type?: string;
  image?: string;
  jsonLd?: object | object[];
}

const BASE_URL = "https://sycmahg.com";
const DEFAULT_IMAGE = "https://sgsrmuvkerbigfdltism.supabase.co/storage/v1/object/public/public-assets/og-image.jpg";

export default function SEOHead({ title, description, path, type = "website", image, jsonLd }: SEOHeadProps) {
  const fullUrl = `${BASE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;
  const fullTitle = path === "/" ? title : `${title} | SYCMA Corporate Finance`;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("og:title", fullTitle, true);
    setMeta("og:description", description, true);
    setMeta("og:url", fullUrl, true);
    setMeta("og:type", type, true);
    setMeta("og:image", ogImage, true);
    setMeta("og:site_name", "SYCMA Corporate Finance", true);
    setMeta("og:locale", "es_ES", true);
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);
    setMeta("twitter:card", "summary_large_image");

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = fullUrl;

    const existingScripts = document.querySelectorAll('script[data-seo="true"]');
    existingScripts.forEach(s => s.remove());

    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach(item => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo", "true");
        script.textContent = JSON.stringify(item);
        document.head.appendChild(script);
      });
    }

    return () => {
      const scripts = document.querySelectorAll('script[data-seo="true"]');
      scripts.forEach(s => s.remove());
    };
  }, [fullTitle, description, fullUrl, type, ogImage, jsonLd]);

  return null;
}

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "SYCMA Corporate Finance",
  alternateName: "SYCMA Holding Group",
  url: BASE_URL,
  logo: "https://storage.googleapis.com/gpt-engineer-file-uploads/1ou0sIJFh5gQbN4UYpEwNCpJ2o83/uploads/1770140688645-LOGO COLOR.png",
  description: "Firma boutique de banca de inversión y finanzas corporativas para empresas hispanohablantes en Americas y Europa. Más de US$10 billones en transacciones.",
  foundingDate: "2020",
  numberOfEmployees: {
    "@type": "QuantitativeValue",
    minValue: 3,
  },
  address: [
    {
      "@type": "PostalAddress",
      addressLocality: "Bogotá",
      addressCountry: "CO",
    },
    {
      "@type": "PostalAddress",
      addressLocality: "Miami",
      addressRegion: "FL",
      addressCountry: "US",
    },
    {
      "@type": "PostalAddress",
      addressLocality: "Madrid",
      addressCountry: "ES",
    },
  ],
  sameAs: [
    "https://www.linkedin.com/company/sycma-holding-group/",
    "https://www.linkedin.com/in/jairo-o-vacca-b-7591a249",
    "https://www.linkedin.com/in/fernando-oliver-sanabria-ovalle-72142630",
    "https://www.linkedin.com/in/daniel-barrera-barrera-6b4a65b8",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    email: "comercial@sycmahg.com",
    telephone: "+57-315-341-8838",
    contactType: "sales",
    availableLanguage: ["Spanish", "English"],
  },
  knowsAbout: [
    "Investment Banking",
    "Mergers and Acquisitions",
    "Corporate Finance",
    "CFO as a Service",
    "Private Equity",
    "Venture Capital",
    "Project Finance",
    "Energy Finance",
  ],
};

export const serviceSchemas = [
  {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    name: "SYCMA Investment Banking & M&A Advisory",
    url: `${BASE_URL}/servicios/investment-banking`,
    provider: { "@type": "Organization", name: "SYCMA Corporate Finance" },
    description: "Asesoría en fusiones y adquisiciones, valoración de empresas, levantamiento de capital y estructuración de transacciones. Más de US$10B en transacciones acompañadas.",
    areaServed: ["Americas", "Europe"],
    serviceType: "Investment Banking Advisory",
  },
  {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    name: "SYCMA Corporate Finance & FP&A",
    url: `${BASE_URL}/servicios/corporate-finance`,
    provider: { "@type": "Organization", name: "SYCMA Corporate Finance" },
    description: "Planeación financiera, KPIs, reportes ejecutivos, modelos financieros y análisis de rentabilidad para empresas hispanohablantes.",
    areaServed: ["Americas", "Europe"],
    serviceType: "Corporate Finance & FP&A",
  },
  {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    name: "SYCMA CFO as a Service",
    url: `${BASE_URL}/servicios/cfo-as-a-service`,
    provider: { "@type": "Organization", name: "SYCMA Corporate Finance" },
    description: "Dirección financiera a tiempo parcial o completo: tesorería, FP&A, reportería ejecutiva, control interno. Sin el costo de un ejecutivo full-time.",
    areaServed: ["Americas", "Europe"],
    serviceType: "CFO as a Service",
  },
  {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    name: "SYCMA Vinculación de Capital o Deuda",
    url: `${BASE_URL}/servicios/capital-y-deuda`,
    provider: { "@type": "Organization", name: "SYCMA Corporate Finance" },
    description: "Acceso a fondos internacionales de private equity, venture capital, créditos de largo plazo con bancos y organismos multilaterales como BID y Banco Mundial.",
    areaServed: ["Americas", "Europe"],
    serviceType: "Capital & Debt Advisory",
  },
];

export const teamPersonSchemas = [
  {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Jairo Vacca",
    jobTitle: "Managing Partner",
    worksFor: { "@type": "Organization", name: "SYCMA Corporate Finance" },
    alumniOf: [
      { "@type": "CollegeOrUniversity", name: "Columbia University" },
      { "@type": "CollegeOrUniversity", name: "MIT" },
      { "@type": "CollegeOrUniversity", name: "Cornell University" },
      { "@type": "CollegeOrUniversity", name: "Harvard University" },
    ],
    knowsAbout: ["Investment Banking", "M&A", "Corporate Finance", "Energy", "Infrastructure"],
    sameAs: "https://www.linkedin.com/in/jairo-o-vacca-b-7591a249",
  },
  {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Fernando Sanabria",
    jobTitle: "Partner",
    worksFor: { "@type": "Organization", name: "SYCMA Corporate Finance" },
    alumniOf: [
      { "@type": "CollegeOrUniversity", name: "Escuela de Negocios de Madrid" },
    ],
    knowsAbout: ["FP&A", "Treasury", "Corporate Finance", "Capital Structuring"],
    sameAs: "https://www.linkedin.com/in/fernando-oliver-sanabria-ovalle-72142630",
  },
  {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Daniel Barrera",
    jobTitle: "Partner",
    worksFor: { "@type": "Organization", name: "SYCMA Corporate Finance" },
    knowsAbout: ["Corporate Finance", "M&A", "Renewable Energy", "Project Finance"],
    sameAs: "https://www.linkedin.com/in/daniel-barrera-barrera-6b4a65b8",
  },
];

export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "¿Qué servicios ofrece SYCMA Corporate Finance?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "SYCMA ofrece Investment Banking Advisory (M&A, capital raising, valoración), Corporate Finance & FP&A (planeación financiera, KPIs, modelos), CFO as a Service (dirección financiera, tesorería, control interno), Vinculación de Capital o Deuda (private equity, venture capital, multilaterales), Consultoría Legal Especializada y Reestructuración Empresarial.",
      },
    },
    {
      "@type": "Question",
      name: "¿En qué países opera SYCMA?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "SYCMA opera desde Miami, Madrid, Ciudad de México, Buenos Aires y Bogotá, atendiendo empresas hispanohablantes en toda la región de Americas y Europa.",
      },
    },
    {
      "@type": "Question",
      name: "¿Qué experiencia tiene el equipo de SYCMA?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Más de 60 años de experiencia combinada en banca de inversión internacional. Socios formados en Columbia, MIT, Cornell, Harvard y Escuela de Negocios de Madrid, con trayectoria en Lloyds TSB, Davivienda y Banco Mundial. Más de US$10 billones en transacciones acompañadas.",
      },
    },
    {
      "@type": "Question",
      name: "¿Qué sectores atiende SYCMA?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "SYCMA tiene experiencia en energía, gas, petróleo, infraestructura, logística, agroindustria, banca, seguros, fiducia, inmobiliario, finanzas verdes y transición energética.",
      },
    },
  ],
};
